import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';

const TIPOS_CONTRATO = ['indefinido', 'temporal', 'por_obra', 'tiempo_parcial'];
const TIPOS_PAGO     = ['mensual', 'quincenal', 'semanal', 'por_hora', 'por_dia'];

const EMPTY_EMP = {
  nombre: '', cedula: '', cargo_id: '', departamento_id: '',
  tipo_contrato: 'indefinido', tipo_pago: 'mensual', salario_base: '',
  banco: '', cuenta_bancaria: '', tipo_cuenta: 'ahorro',
  email: '', telefono: '', direccion: '', fecha_ingreso: '', notas: '',
};

export default function Empleados() {
  const [empleados, setEmpleados]       = useState([]);
  const [cargos, setCargos]             = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [buscar, setBuscar]             = useState('');
  const [soloActivos, setSoloActivos]   = useState(true);
  const [loading, setLoading]           = useState(false);

  // Modal empleado
  const [modalOpen, setModalOpen]   = useState(false);
  const [editando, setEditando]     = useState(null); // null = nuevo
  const [form, setForm]             = useState(EMPTY_EMP);

  // Modal cargo
  const [modalCargo, setModalCargo] = useState(false);
  const [nuevoCargo, setNuevoCargo] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { activo: soloActivos };
      if (buscar) params.buscar = buscar;
      const { data } = await api.get('/empleados', { params });
      setEmpleados(data);
    } catch { } finally { setLoading(false); }
  }, [buscar, soloActivos]);

  useEffect(() => {
    cargar();
    api.get('/empleados/cargos').then(r => setCargos(r.data)).catch(() => {});
    api.get('/departamentos').then(r => setDepartamentos(r.data)).catch(() => {});
  }, [cargar]);

  const abrirNuevo = () => { setEditando(null); setForm(EMPTY_EMP); setModalOpen(true); };
  const abrirEditar = (emp) => {
    setEditando(emp.id);
    setForm({
      nombre: emp.nombre || '', cedula: emp.cedula || '',
      cargo_id: emp.cargo_id || '', departamento_id: emp.departamento_id || '',
      tipo_contrato: emp.tipo_contrato || 'indefinido',
      tipo_pago: emp.tipo_pago || 'mensual',
      salario_base: emp.salario_base || '',
      banco: emp.banco || '', cuenta_bancaria: emp.cuenta_bancaria || '',
      tipo_cuenta: emp.tipo_cuenta || 'ahorro',
      email: emp.email || '', telefono: emp.telefono || '',
      direccion: emp.direccion || '', fecha_ingreso: emp.fecha_ingreso || '',
      notas: emp.notas || '',
    });
    setModalOpen(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return Swal.fire('Error', 'El nombre es requerido', 'error');
    try {
      if (editando) {
        await api.put(`/empleados/${editando}`, form);
      } else {
        await api.post('/empleados', form);
      }
      Swal.fire({ icon: 'success', title: editando ? 'Actualizado' : 'Creado', timer: 1500, showConfirmButton: false });
      setModalOpen(false);
      cargar();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'Error al guardar', 'error');
    }
  };

  const desactivar = async (emp) => {
    const r = await Swal.fire({
      title: `¿Desactivar a ${emp.nombre}?`,
      text: 'El empleado quedará inactivo pero sus registros se conservan.',
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Sí, desactivar', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
    });
    if (!r.isConfirmed) return;
    try {
      await api.delete(`/empleados/${emp.id}`);
      Swal.fire({ icon: 'success', title: 'Desactivado', timer: 1200, showConfirmButton: false });
      cargar();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'Error al desactivar', 'error');
    }
  };

  const guardarCargo = async () => {
    if (!nuevoCargo.trim()) return;
    try {
      const { data } = await api.post('/empleados/cargos', { nombre: nuevoCargo });
      setCargos(prev => [...prev, data]);
      setNuevoCargo('');
      setModalCargo(false);
      Swal.fire({ icon: 'success', title: 'Cargo creado', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'Error al crear cargo', 'error');
    }
  };

  const inp = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // ── Estilos inline rápidos ────────────────────────────────────────────────
  const st = {
    page:   { padding: '24px', fontFamily: 'sans-serif', maxWidth: 1100, margin: '0 auto' },
    card:   { background: '#fff', borderRadius: 12, boxShadow: '0 2px 10px #0001', padding: 24, marginBottom: 24 },
    btn:    { padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
    badge:  (c) => ({ background: c, color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: 12 }),
    tbl:    { width: '100%', borderCollapse: 'collapse' },
    th:     { textAlign: 'left', padding: '10px 12px', background: '#f8f9fa', borderBottom: '2px solid #dee2e6', fontSize: 13 },
    td:     { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', fontSize: 14 },
    input:  { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' },
    select: { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 },
    overlay:{ position: 'fixed', inset: 0, background: '#0006', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    modal:  { background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'auto' },
    grid2:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
    label:  { display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600, color: '#444' },
  };

  const ESTADO_COLOR = { indefinido: '#28a745', temporal: '#fd7e14', por_obra: '#6610f2', tiempo_parcial: '#17a2b8' };

  return (
    <div style={st.page}>
      {/* Cabecera */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0 }}>👷 Empleados</h2>
          <p style={{ margin: 0, color: '#888' }}>Directorio global de empleados</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={{ ...st.btn, background: '#6c757d', color: '#fff' }} onClick={() => setModalCargo(true)}>
            + Nuevo Cargo
          </button>
          <button style={{ ...st.btn, background: '#0d6efd', color: '#fff' }} onClick={abrirNuevo}>
            + Nuevo Empleado
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div style={st.card}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            style={{ ...st.input, maxWidth: 280 }}
            placeholder="Buscar por nombre, cédula o código..."
            value={buscar} onChange={e => setBuscar(e.target.value)}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={soloActivos} onChange={e => setSoloActivos(e.target.checked)} />
            Solo activos
          </label>
          <button style={{ ...st.btn, background: '#0d6efd', color: '#fff' }} onClick={cargar}>Buscar</button>
        </div>
      </div>

      {/* Tabla */}
      <div style={st.card}>
        {loading ? <p style={{ textAlign: 'center', color: '#888' }}>Cargando...</p> : (
          <table style={st.tbl}>
            <thead>
              <tr>
                {['Código','Nombre','Cédula','Cargo','Tipo Contrato','Salario Base','Estado','Acciones'].map(h => (
                  <th key={h} style={st.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {empleados.length === 0 && (
                <tr><td colSpan={8} style={{ ...st.td, textAlign: 'center', color: '#aaa' }}>Sin resultados</td></tr>
              )}
              {empleados.map(emp => (
                <tr key={emp.id} style={{ background: emp.activo ? '#fff' : '#f8f9fa' }}>
                  <td style={st.td}><code style={{ fontSize: 12 }}>{emp.codigo || '-'}</code></td>
                  <td style={st.td}><strong>{emp.nombre}</strong></td>
                  <td style={st.td}>{emp.cedula || '-'}</td>
                  <td style={st.td}>{emp.cargo?.nombre || '-'}</td>
                  <td style={st.td}>
                    <span style={st.badge(ESTADO_COLOR[emp.tipo_contrato] || '#888')}>
                      {emp.tipo_contrato}
                    </span>
                  </td>
                  <td style={st.td}>RD$ {Number(emp.salario_base || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</td>
                  <td style={st.td}>
                    <span style={st.badge(emp.activo ? '#28a745' : '#dc3545')}>
                      {emp.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td style={st.td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={{ ...st.btn, background: '#0d6efd', color: '#fff', padding: '5px 12px' }}
                        onClick={() => abrirEditar(emp)}>Editar</button>
                      {emp.activo && (
                        <button style={{ ...st.btn, background: '#dc3545', color: '#fff', padding: '5px 12px' }}
                          onClick={() => desactivar(emp)}>Desactivar</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal empleado */}
      {modalOpen && (
        <div style={st.overlay} onClick={e => e.target === e.currentTarget && setModalOpen(false)}>
          <div style={st.modal}>
            <h3 style={{ marginTop: 0 }}>{editando ? 'Editar Empleado' : 'Nuevo Empleado'}</h3>
            <form onSubmit={guardar}>
              <div style={st.grid2}>
                <div>
                  <label style={st.label}>Nombre *</label>
                  <input name="nombre" value={form.nombre} onChange={inp} style={st.input} required />
                </div>
                <div>
                  <label style={st.label}>Cédula</label>
                  <input name="cedula" value={form.cedula} onChange={inp} style={st.input} placeholder="000-0000000-0" />
                </div>
                <div>
                  <label style={st.label}>Cargo</label>
                  <select name="cargo_id" value={form.cargo_id} onChange={inp} style={st.select}>
                    <option value="">-- Sin cargo --</option>
                    {cargos.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label style={st.label}>Departamento</label>
                  <select name="departamento_id" value={form.departamento_id} onChange={inp} style={st.select}>
                    <option value="">-- Sin departamento --</option>
                    {departamentos.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label style={st.label}>Tipo de Contrato</label>
                  <select name="tipo_contrato" value={form.tipo_contrato} onChange={inp} style={st.select}>
                    {TIPOS_CONTRATO.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={st.label}>Tipo de Pago</label>
                  <select name="tipo_pago" value={form.tipo_pago} onChange={inp} style={st.select}>
                    {TIPOS_PAGO.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={st.label}>Salario Base (RD$)</label>
                  <input type="number" name="salario_base" value={form.salario_base} onChange={inp} style={st.input} min={0} step={0.01} />
                </div>
                <div>
                  <label style={st.label}>Fecha de Ingreso</label>
                  <input type="date" name="fecha_ingreso" value={form.fecha_ingreso} onChange={inp} style={st.input} />
                </div>
              </div>

              <p style={{ fontWeight: 700, color: '#555', marginBottom: 8 }}>Información bancaria</p>
              <div style={st.grid2}>
                <div>
                  <label style={st.label}>Banco</label>
                  <input name="banco" value={form.banco} onChange={inp} style={st.input} />
                </div>
                <div>
                  <label style={st.label}>Número de Cuenta</label>
                  <input name="cuenta_bancaria" value={form.cuenta_bancaria} onChange={inp} style={st.input} />
                </div>
                <div>
                  <label style={st.label}>Tipo de Cuenta</label>
                  <select name="tipo_cuenta" value={form.tipo_cuenta} onChange={inp} style={st.select}>
                    <option value="ahorro">Ahorro</option>
                    <option value="corriente">Corriente</option>
                  </select>
                </div>
                <div>
                  <label style={st.label}>Email</label>
                  <input type="email" name="email" value={form.email} onChange={inp} style={st.input} />
                </div>
                <div>
                  <label style={st.label}>Teléfono</label>
                  <input name="telefono" value={form.telefono} onChange={inp} style={st.input} />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={st.label}>Dirección</label>
                <input name="direccion" value={form.direccion} onChange={inp} style={st.input} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={st.label}>Notas</label>
                <textarea name="notas" value={form.notas} onChange={inp}
                  style={{ ...st.input, height: 60, resize: 'vertical' }} />
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" style={{ ...st.btn, background: '#6c757d', color: '#fff' }}
                  onClick={() => setModalOpen(false)}>Cancelar</button>
                <button type="submit" style={{ ...st.btn, background: '#28a745', color: '#fff' }}>
                  {editando ? 'Actualizar' : 'Crear Empleado'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal nuevo cargo */}
      {modalCargo && (
        <div style={st.overlay} onClick={e => e.target === e.currentTarget && setModalCargo(false)}>
          <div style={{ ...st.modal, maxWidth: 400 }}>
            <h3 style={{ marginTop: 0 }}>Nuevo Cargo</h3>
            <label style={st.label}>Nombre del cargo</label>
            <input style={{ ...st.input, marginBottom: 20 }} value={nuevoCargo}
              onChange={e => setNuevoCargo(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && guardarCargo()}
              autoFocus placeholder="Ej: Supervisor, Operario, Contador..." />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={{ ...st.btn, background: '#6c757d', color: '#fff' }}
                onClick={() => setModalCargo(false)}>Cancelar</button>
              <button style={{ ...st.btn, background: '#28a745', color: '#fff' }}
                onClick={guardarCargo}>Crear</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
